import './coapplicantSameResidencePage.scss';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import { ThemeProvider } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import isEmpty from 'lodash/isEmpty';
import toString from 'lodash/toString';
import { ReactNode } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { inputEventHandler } from '../../../components/utils/inputEventHandler';
import useCoapplicantStore from '../../../store/coapplicantStore/useCoapplicantStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { next, previous } = useWorkflowStore();
  const { t } = useTranslation();

  const {
    computed: { isCoapplicantPersisted },
    coapplicantCurrentResidenceMonthlyPaymentAmount,
    isSyncingCoapplicant,
    syncingCoapplicantFailedMessage,
    setCoapplicantCurrentResidenceMonthlyPaymentAmount,
    syncCoapplicant,
  } = useCoapplicantStore();

  const {
    register,
    handleSubmit,
    setValue,
    trigger,
    formState: { errors, isDirty, isValid },
  } = useForm({
    criteriaMode: 'firstError',
    defaultValues: {
      housingAmountValue: inputEventHandler.currency.formatter(
        coapplicantCurrentResidenceMonthlyPaymentAmount,
      ),
    },
    delayError: 200,
    mode: 'onBlur',
    shouldFocusError: false,
  });

  const housingAmountValidation = {
    setValueAs: (value: string): string =>
      inputEventHandler.currency.parser(value),
    required: t('validation.housingAmount.required'),
    min: {
      value: 0,
      message: t('validation.housingAmount.min', { min: '$0' }),
    },
    max: {
      value: 1000000,
      message: t('validation.housingAmount.max', { max: '$1,000,000' }),
    },
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { housingAmountValue } = values;

    if (!housingAmountValue) {
      return;
    }

    setCoapplicantCurrentResidenceMonthlyPaymentAmount(housingAmountValue);

    if (isCoapplicantPersisted && isDirty) {
      const synced = await syncCoapplicant();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  return (
    <ThemeProvider theme={theme}>
      <Box className="coapplicant-same-residence-page" sx={theme.pageLayout}>
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.coapplicantSameResidence.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.coapplicantSameResidence.form.h1}>
                {t('CoapplicantSameResidencePage.title')}
              </h1>
              <h2 data-testid={locator.pages.coapplicantSameResidence.form.h2}>
                {t('CoapplicantSameResidencePage.subtitle')}
              </h2>

              {!isEmpty(syncingCoapplicantFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert icon={false} severity="error">
                    {syncingCoapplicantFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <TextField
                  {...register('housingAmountValue', housingAmountValidation)}
                  error={!isEmpty(errors.housingAmountValue)}
                  label={t(
                    'CoapplicantSameResidencePage.coapplicantHousingAmount',
                  )}
                  slotProps={{
                    htmlInput: {
                      'data-testid':
                        locator.pages.coapplicantSameResidence.form
                          .housingAmountInput,
                      inputMode: 'numeric',
                    },
                  }}
                  type="text"
                  variant="outlined"
                  fullWidth
                  onBlur={inputEventHandler.currency.blur(
                    'housingAmountValue',
                    setValue,
                    trigger,
                  )}
                  onChange={inputEventHandler.currency.change(
                    'housingAmountValue',
                    setValue,
                  )}
                  onFocus={inputEventHandler.currency.focus(
                    'housingAmountValue',
                    setValue,
                  )}
                  onKeyDown={inputEventHandler.number.keydown}
                  onPasteCapture={inputEventHandler.number.pasteCapture}
                />

                {!isEmpty(errors.housingAmountValue) && (
                  <span
                    className="field-error"
                    data-testid={
                      locator.pages.coapplicantSameResidence.form
                        .housingAmountInputError
                    }
                  >
                    {toString(errors.housingAmountValue.message)}
                  </span>
                )}
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.coapplicantSameResidence.form.submitButton
                  }
                  disabled={!isValid}
                  loader={isSyncingCoapplicant}
                  fullWidth
                  submit
                >
                  {t('CoapplicantSameResidencePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.coapplicantSameResidence.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'CoapplicantSameResidencePage';
